import type { FC } from 'react';
import React from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';

import { AiPolicyFooter } from './AiPolicyFooter.tsx';

type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const styles = cssMap({
	footer: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		position: 'relative',
		zIndex: 1,
		width: '100%',
	},
	solid: {
		height: '45px',
		backgroundColor: token('elevation.surface.overlay'),
		backgroundImage: 'none',
	},
	populated: {
		alignItems: 'flex-end',
		paddingBlockEnd: token('space.050'),
		height: '48px',
		marginBlockStart: `calc(-1 * ${token('space.600')})`,
		backgroundColor: 'transparent',
		backgroundImage: `linear-gradient(0deg, ${token('elevation.surface.overlay')} 47.5%, transparent 100%)`,
	},
});

/**
 * This component is only used under platform_editor_ai_content_suggested_edits
 * at the moment.
 */
export const SuggestionsFooter: FC = () => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const { isPopulated } = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		(state: { aiSuggestionsState?: AISuggestionsSharedState }) => ({
			isPopulated:
				(state?.aiSuggestionsState?.orderedSuggestionIds?.length ?? 0) > 0 &&
				state?.aiSuggestionsState?.thinkingStatus !== 'thinking',
		}),
	);

	return (
		<footer
			css={[styles.footer, isPopulated ? styles.populated : styles.solid]}
			data-testid="suggestions-panel-footer"
		>
			<AiPolicyFooter />
		</footer>
	);
};
